import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isDraftDate, toLocalInput, scheduleForSave, DRAFT_HINT, scheduleBadgeText, scheduleFieldLabel } from '../lib/scheduleDraft';
import axios from 'axios';
import { avatarUrl } from '../utils/fileUrl';
import { toast } from 'sonner';
import { registerAudio } from '../lib/audioManager';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AiFeedbackButton from '../components/AiFeedbackButton';
import {
  PencilLine,
  UsersThree,
  Microphone,
  Stop,
  Play,
  Pause,
  Heart,
  ChatCircle,
  PaperPlaneTilt,
  SpinnerGap,
  Waveform,
  Plus,
  MagnifyingGlass,
  PencilSimpleLine,
  Trash,
  CheckCircle,
  FloppyDisk,
  X as XIcon
} from '@phosphor-icons/react';
import WritingMilestoneMeter from '../components/WritingMilestoneMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

// Small multi-level selector (keeps at least one level selected)
const LevelPills = ({ value = ['A2'], onChange, testIdPrefix = 'topic-level' }) => {
  const toggle = (lvl) => {
    const has = value.includes(lvl);
    let next = has ? value.filter(l => l !== lvl) : [...value, lvl];
    if (next.length === 0) next = [lvl];
    next = CEFR_LEVELS.filter(l => next.includes(l));
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-pills`}>
      {CEFR_LEVELS.map(l => {
        const active = value.includes(l);
        return (
          <button
            key={l} type="button" onClick={() => toggle(l)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${active
              ? 'bg-[#AB1C24] text-white border-[#AB1C24]'
              : 'bg-white text-[#78716C] border-stone-300 hover:border-[#AB1C24] hover:text-[#AB1C24]'}`}
            data-testid={`${testIdPrefix}-pill-${l}`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
};

const CommunityPage = ({ mode = 'community' } = {}) => {
  const { user } = useAuth();
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const isWriting = mode === 'writing';
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [writtenPosts, setWrittenPosts] = useState([]);
  const [voicePosts, setVoicePosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('written');

  // In writing mode, voice tab is disabled — force back to written if somehow set.
  useEffect(() => {
    if (mode === 'writing' && activeTab !== 'written') setActiveTab('written');
  }, [mode, activeTab]);

  // Board view preferences
  const [topicLimit, setTopicLimit] = useState(10); // 10 / 50 / 100
  const [postLimit, setPostLimit] = useState(10); // 10 / 50 / 100
  const [topicSearch, setTopicSearch] = useState('');

  // New-topic form (teachers only) — now multi-level
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({
    title: '', content: '', content_de: '', prompt_question: '', levels: ['A2'],
    topic_type: 'open', options: ['', ''], correct_option: null,
    scheduled_at: '', group_id: '',
  });
  const [creatingTopic, setCreatingTopic] = useState(false);
  // Teacher-only groups for resend + scheduling scope. Loaded lazily.
  const [groupsList, setGroupsList] = useState([]);
  // Resend state
  const [resendTopic, setResendTopic] = useState(null); // { topic }
  const [resendSel, setResendSel] = useState(new Set());
  const [resendBusy, setResendBusy] = useState(false);
  useEffect(() => {
    setResendSel(new Set());
  }, [resendTopic]);
  useEffect(() => {
    const isStaff = user?.role === 'admin' || user?.role === 'teacher';
    if (!isStaff) return;
    axios.get(`${API}/groups`, { withCredentials: true })
      .then(r => setGroupsList(r.data || []))
      .catch(() => setGroupsList([]));
  }, [user]);

  // Teacher edit-topic state
  const [editingTopic, setEditingTopic] = useState(false);
  const [topicDraft, setTopicDraft] = useState(null);

  // Inline reply + edit state for posts
  const [replyTo, setReplyTo] = useState(null);       // post_id
  const [replyText, setReplyText] = useState('');
  const [editingPostId, setEditingPostId] = useState(null);
  const [editPostText, setEditPostText] = useState('');

  // Multiple-choice answer selection (for student)
  const [selectedMcOption, setSelectedMcOption] = useState(null);

  // Optional text caption for voice posts
  const [voiceCaption, setVoiceCaption] = useState('');

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicLimit]);

  // Deep link from the teacher's "Geplante Veröffentlichungen": ?topic=ID&edit=1
  const [searchParams] = useSearchParams();
  const deepTopicId = searchParams.get('topic');
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (!deepTopicId || deepLinkDone.current || !topics.length) return;
    const t = topics.find(x => x.topic_id === deepTopicId);
    if (!t) {
      if (topicLimit < 100) setTopicLimit(100);
      return;
    }
    if (selectedTopic?.topic_id !== t.topic_id) { setSelectedTopic(t); return; }
    deepLinkDone.current = true;
    if (searchParams.get('edit') === '1' && isTeacher) startEditTopic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, selectedTopic, deepTopicId]);

  useEffect(() => {
    if (selectedTopic) {
      loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopic, activeTab, postLimit]);

  const loadTopics = async () => {
    try {
      const cat = isWriting ? 'writing' : 'community';
      const res = await axios.get(`${API}/topics?limit=${topicLimit}&category=${cat}`, { withCredentials: true });
      setTopics(res.data);
      // Preserve selection if still present, otherwise pick first
      if (res.data.length > 0) {
        setSelectedTopic(prev => res.data.find(t => t.topic_id === prev?.topic_id) || res.data[0]);
      } else {
        setSelectedTopic(null);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Themen');
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    if (!selectedTopic) return;

    try {
      if (activeTab === 'written') {
        const res = await axios.get(`${API}/forum/posts/${selectedTopic.topic_id}?limit=${postLimit}`, { withCredentials: true });
        setWrittenPosts(res.data);
      } else {
        const res = await axios.get(`${API}/forum/voice/${selectedTopic.topic_id}?limit=${postLimit}`, { withCredentials: true });
        setVoicePosts(res.data);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const handleCreateTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.content.trim()) {
      toast.error('Titel und Frage erforderlich');
      return;
    }
    const payload = {
      title: newTopic.title,
      content: newTopic.content,
      content_de: newTopic.content_de || null,
      prompt_question: newTopic.prompt_question || newTopic.content,
      prompt_question_de: null,
      levels: newTopic.levels,
      topic_type: newTopic.topic_type,
      category: isWriting ? 'writing' : 'community',
    };
    // Optional scoping + scheduling — empty means "publish now, all classes".
    if (newTopic.group_id) payload.group_id = newTopic.group_id;
    if (newTopic.scheduled_at) {
      // datetime-local is teacher's local time. Convert to UTC ISO so the
      // scheduled_publish_loop compares against the same wall clock.
      try { payload.scheduled_at = new Date(newTopic.scheduled_at).toISOString(); }
      catch { /* noop */ }
    }
    if (newTopic.topic_type === 'multiple_choice') {
      const cleanOpts = newTopic.options.map(o => o.trim()).filter(Boolean);
      if (cleanOpts.length < 2) {
        toast.error('Mindestens 2 Optionen angeben');
        return;
      }
      payload.options = cleanOpts;
      payload.correct_option = newTopic.correct_option;
    }
    setCreatingTopic(true);
    try {
      const res = await axios.post(`${API}/topics`, payload, { withCredentials: true });
      toast.success(payload.scheduled_at ? 'Thema für den geplanten Zeitpunkt eingeplant' : 'Thema veröffentlicht');
      setShowNewTopic(false);
      setNewTopic({ title: '', content: '', content_de: '', prompt_question: '', levels: ['A2'], topic_type: 'open', options: ['', ''], correct_option: null, scheduled_at: '', group_id: '' });
      await loadTopics();
      setSelectedTopic(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Erstellen');
    } finally {
      setCreatingTopic(false);
    }
  };

  const startEditTopic = () => {
    if (!selectedTopic) return;
    const currentLevels = selectedTopic.levels && selectedTopic.levels.length > 0
      ? selectedTopic.levels
      : [selectedTopic.level || 'A2'];
    setTopicDraft({
      title: selectedTopic.title || '',
      content: selectedTopic.content || '',
      content_de: selectedTopic.content_de || '',
      prompt_question: selectedTopic.prompt_question || '',
      levels: currentLevels,
      topic_type: selectedTopic.topic_type || 'open',
      options: selectedTopic.options && selectedTopic.options.length >= 2 ? [...selectedTopic.options] : ['', ''],
      correct_option: selectedTopic.correct_option ?? null,
      scheduled_at: toLocalInput(selectedTopic.scheduled_at),
    });
    setEditingTopic(true);
  };

  const saveEditTopic = async () => {
    if (!topicDraft) return;
    const payload = {
      title: topicDraft.title,
      content: topicDraft.content,
      content_de: topicDraft.content_de || null,
      prompt_question: topicDraft.prompt_question,
      levels: topicDraft.levels,
      topic_type: topicDraft.topic_type,
      scheduled_at: scheduleForSave(topicDraft.scheduled_at, selectedTopic.scheduled_at),
    };
    if (topicDraft.topic_type === 'multiple_choice') {
      payload.options = topicDraft.options.map(o => o.trim()).filter(Boolean);
      payload.correct_option = topicDraft.correct_option;
    } else {
      payload.options = null;
      payload.correct_option = null;
    }
    try {
      const res = await axios.patch(`${API}/topics/${selectedTopic.topic_id}`, payload, { withCredentials: true });
      toast.success('Thema aktualisiert');
      setEditingTopic(false);
      await loadTopics();
      setSelectedTopic(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const deleteTopic = async () => {
    if (!selectedTopic) return;
    if (!window.confirm(`¿Eliminar el tema "${selectedTopic.title}" y TODAS sus respuestas?`)) return;
    try {
      await axios.delete(`${API}/topics/${selectedTopic.topic_id}`, { withCredentials: true });
      toast.success('Tema eliminado');
      await loadTopics();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const submitReply = async (parentId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/forum/posts`, {
        topic_id: selectedTopic.topic_id,
        content: replyText.trim(),
        parent_id: parentId,
      }, { withCredentials: true });
      setReplyTo(null);
      setReplyText('');
      loadPosts();
      if (isTeacher) loadTopics();
      toast.success(isTeacher ? 'Respuesta enviada al alumno' : 'Respuesta publicada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const savePostEdit = async (postId) => {
    if (!editPostText.trim()) return;
    try {
      await axios.patch(`${API}/forum/posts/${postId}`, { content: editPostText.trim() }, { withCredentials: true });
      setEditingPostId(null);
      setEditPostText('');
      loadPosts();
      toast.success('Aktualisiert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('¿Eliminar este comentario (y sus respuestas)?')) return;
    try {
      await axios.delete(`${API}/forum/posts/${postId}`, { withCredentials: true });
      loadPosts();
      toast.success('Eliminado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleSubmitPost = async () => {
    if (!newPost.trim()) {
      toast.error('Bitte schreibe etwas');
      return;
    }
    if (!selectedTopic) {
      toast.error('Kein Thema ausgewählt');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { topic_id: selectedTopic.topic_id, content: newPost };
      if (selectedTopic.topic_type === "multiple_choice") {
        if (selectedMcOption === null || selectedMcOption === undefined) {
          toast.error("Bitte wähle eine Option");
          setSubmitting(false);
          return;
        }
        payload.selected_option = selectedMcOption;
      }
      const res = await axios.post(`${API}/forum/posts`, payload, {
        withCredentials: true,
      });

      const milestone = res.data?.milestone;
      if (isWriting && milestone?.reached) {
        toast.success(
          `Beitrag veröffentlicht! +${5 + (milestone.bonus || 5)} Punkte (Wortziel erreicht)`,
        );
      } else if (isWriting && milestone) {
        toast.success(
          `Beitrag veröffentlicht! +5 Punkte — noch ${Math.max(0, milestone.target - milestone.word_count)} Wörter bis zum Bonus`,
        );
      } else {
        toast.success("Beitrag veröffentlicht! +5 Punkte");
      }
      setNewPost("");
      setSelectedMcOption(null);
      loadPosts();
      if (isTeacher) loadTopics();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      const msg = typeof detail === 'string' ? detail
        : !status ? 'Verbindungsfehler. Bitte Internetverbindung prüfen.'
        : status === 401 ? 'Bitte melde dich erneut an.'
        : `Fehler ${status}`;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/forum/posts/${postId}/like`, {}, { withCredentials: true });
      if (activeTab === 'written') {
        setWrittenPosts(writtenPosts.map(p => 
          p.post_id === postId 
            ? { ...p, likes: res.data.liked ? p.likes + 1 : p.likes - 1 }
            : p
        ));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler');
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      toast.error('Mikrofon-Zugriff verweigert');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const submitVoicePost = async () => {
    if (!audioBlob) {
      toast.error('Bitte nimm erst etwas auf');
      return;
    }
    if (!selectedTopic) {
      toast.error('Kein Thema ausgewählt');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice.webm');
      if (voiceCaption.trim()) formData.append('content', voiceCaption.trim());

      await axios.post(`${API}/forum/voice?topic_id=${selectedTopic.topic_id}`, formData, {
        withCredentials: true,
      });
      
      toast.success('Sprachaufnahme gesendet! +5 Punkte');
      setAudioBlob(null);
      setAudioUrl(null);
      setVoiceCaption('');
      loadPosts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      const msg = typeof detail === 'string' ? detail
        : !status ? 'Verbindungsfehler beim Audio-Upload. Bitte nochmal versuchen.'
        : status === 401 ? 'Bitte melde dich erneut an.'
        : status === 413 ? 'Audio zu groß (max 15 MB).'
        : `Fehler ${status}`;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const playVoice = async (postId, storagePath) => {
    if (isPlaying === postId) {
      audioRef.current?.pause();
      setIsPlaying(null);
      return;
    }

    try {
      const res = await axios.get(`${API}/files/${storagePath}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      audioRef.current = registerAudio(new Audio(url));
      audioRef.current.onended = () => setIsPlaying(null);
      audioRef.current.play();
      setIsPlaying(postId);
    } catch (error) {
      toast.error('Fehler beim Abspielen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <SpinnerGap size={40} className="animate-spin text-[#AB1C24]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={isWriting ? 'schreiben-page' : 'community-page'}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            {isWriting
              ? <PencilLine size={28} weight="duotone" className="text-[#8B5A3C]" />
              : <UsersThree size={28} weight="duotone" className="text-[#5B8C9A]" />}
            {isWriting ? 'Schreiben' : 'Community'}
          </h1>
          <p className="text-[#57534E] mt-1">
            {isWriting
              ? 'Schreibe einen Text zum Thema — dein Lehrer korrigiert ihn.'
              : 'Diskutiere und übe mit anderen Lernenden'}
          </p>
        </div>
        {isTeacher && (
          <Button
            onClick={() => setShowNewTopic(v => !v)}
            className="bg-[#AB1C24] hover:bg-[#8A151C] text-white"
            data-testid="new-topic-btn"
          >
            {showNewTopic ? <XIcon size={18} className="mr-2" /> : <Plus size={18} className="mr-2" />}
            {showNewTopic ? 'Abbrechen' : 'Neues Thema'}
          </Button>
        )}
      </div>

      {/* New Topic Form (teachers only) */}
      {isTeacher && showNewTopic && (
        <div className="bg-white rounded-2xl border border-[#AB1C24]/40 p-6 space-y-3" data-testid="new-topic-form">
          <h3 className="font-heading font-semibold text-lg text-[#292524]">Neues Thema erstellen</h3>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setNewTopic(p => ({ ...p, topic_type: 'open' }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${newTopic.topic_type === 'open' ? 'bg-[#AB1C24] text-white border-[#AB1C24]' : 'bg-white text-[#57534E] border-stone-300'}`}
              data-testid="topic-type-open"
            >Offene Frage</button>
            <button
              type="button"
              onClick={() => setNewTopic(p => ({ ...p, topic_type: 'multiple_choice' }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${newTopic.topic_type === 'multiple_choice' ? 'bg-[#AB1C24] text-white border-[#AB1C24]' : 'bg-white text-[#57534E] border-stone-300'}`}
              data-testid="topic-type-mc"
            >Multiple Choice</button>
          </div>
          <Input
            placeholder="Titel (z.B. Mi comida favorita)"
            value={newTopic.title}
            onChange={e => setNewTopic(p => ({ ...p, title: e.target.value }))}
            data-testid="new-topic-title"
          />
          <Textarea
            placeholder="Einleitung auf Spanisch..."
            value={newTopic.content}
            onChange={e => setNewTopic(p => ({ ...p, content: e.target.value }))}
            className="min-h-[80px]"
            data-testid="new-topic-content"
          />
          <Textarea
            placeholder="Deutsche Übersetzung (optional)"
            value={newTopic.content_de}
            onChange={e => setNewTopic(p => ({ ...p, content_de: e.target.value }))}
            className="min-h-[60px]"
          />
          <Input
            placeholder="Frage an die Teilnehmer"
            value={newTopic.prompt_question}
            onChange={e => setNewTopic(p => ({ ...p, prompt_question: e.target.value }))}
          />
          {newTopic.topic_type === 'multiple_choice' && (
            <div className="space-y-2 bg-[#FAF9F6] rounded-xl p-3" data-testid="mc-options-editor">
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Optionen (2–6). Klicke ✓ für die korrekte.</p>
              {newTopic.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setNewTopic(p => ({ ...p, correct_option: p.correct_option === i ? null : i }))}
                    className={`w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${newTopic.correct_option === i ? 'bg-green-600 border-green-600 text-white' : 'border-stone-300 text-stone-400'}`}
                    title="Als korrekt markieren"
                    data-testid={`mc-correct-${i}`}
                  >
                    <CheckCircle size={14} weight="fill" />
                  </button>
                  <Input
                    placeholder={`Option ${i + 1}`}
                    value={opt}
                    onChange={e => {
                      const opts = [...newTopic.options];
                      opts[i] = e.target.value;
                      setNewTopic(p => ({ ...p, options: opts }));
                    }}
                    data-testid={`mc-option-${i}`}
                  />
                  {newTopic.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setNewTopic(p => ({
                        ...p,
                        options: p.options.filter((_, j) => j !== i),
                        correct_option: p.correct_option === i ? null : p.correct_option !== null && p.correct_option > i ? p.correct_option - 1 : p.correct_option,
                      }))}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <XIcon size={14} />
                    </button>
                  )}
                </div>
              ))}
              {newTopic.options.length < 6 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewTopic(p => ({ ...p, options: [...p.options, ''] }))}
                  data-testid="mc-add-option-btn"
                >
                  <Plus size={14} className="mr-1" /> Option hinzufügen
                </Button>
              )}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Niveaus (mehrere auswählbar)</p>
            <LevelPills value={newTopic.levels} onChange={v => setNewTopic(p => ({ ...p, levels: v }))} testIdPrefix="new-topic-level" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Klasse (optional)</p>
              <select
                value={newTopic.group_id}
                onChange={e => setNewTopic(p => ({ ...p, group_id: e.target.value }))}
                className="w-full text-sm border border-stone-300 rounded-md px-2 py-2 bg-white"
                data-testid="new-topic-group"
              >
                <option value="">— Alle Klassen —</option>
                {groupsList.map(g => <option key={g.group_id} value={g.group_id}>{g.name} · {g.level}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Veröffentlichen am (optional)</p>
              <input
                type="datetime-local"
                value={newTopic.scheduled_at}
                onChange={e => setNewTopic(p => ({ ...p, scheduled_at: e.target.value }))}
                className="w-full text-sm border border-stone-300 rounded-md px-2 py-2 bg-white"
                data-testid="new-topic-scheduled-at"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleCreateTopic}
              disabled={creatingTopic}
              className="bg-[#AB1C24] hover:bg-[#8A151C]"
              data-testid="submit-new-topic-btn"
            >
              {creatingTopic ? <SpinnerGap size={18} className="animate-spin mr-2" /> : <PaperPlaneTilt size={18} className="mr-2" />}
              Veröffentlichen
            </Button>
          </div>
        </div>
      )}

      {/* Board controls: search, topic limit, topic selector */}
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] items-center" data-testid="board-controls">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Themen durchsuchen..."
            value={topicSearch}
            onChange={e => setTopicSearch(e.target.value)}
            className="pl-10"
            data-testid="topic-search-input"
          />
          {topicSearch && (
            <button
              onClick={() => setTopicSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              aria-label="Suche zurücksetzen"
            >
              <XIcon size={14} />
            </button>
          )}
        </div>
        <Select value={String(topicLimit)} onValueChange={v => setTopicLimit(parseInt(v, 10))}>
          <SelectTrigger className="w-[170px]" data-testid="topic-limit-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">Letzte 10 Themen</SelectItem>
            <SelectItem value="50">Letzte 50 Themen</SelectItem>
            <SelectItem value="100">Letzte 100 Themen</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={selectedTopic?.topic_id || ''}
          onValueChange={(value) => setSelectedTopic(topics.find(t => t.topic_id === value))}
        >
          <SelectTrigger className="w-full sm:w-[280px]" data-testid="topic-select">
            <SelectValue placeholder="Thema wählen" />
          </SelectTrigger>
          <SelectContent>
            {(topicSearch.trim()
              ? topics.filter(t => {
                  const q = topicSearch.trim().toLowerCase();
                  return (t.title || '').toLowerCase().includes(q) || (t.content || '').toLowerCase().includes(q);
                })
              : topics
            ).map((topic) => (
              <SelectItem key={topic.topic_id} value={topic.topic_id}>
                <span className="inline-flex items-center gap-2">
                  <span className={`level-badge ${topic.level} text-xs`}>{topic.level}</span>
                  <span>{topic.title}</span>
                  {topic.teacher_responded ? (
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className="text-emerald-600 shrink-0"
                      aria-label="Vom Lehrer korrigiert"
                      data-testid={`topic-corrected-${topic.topic_id}`}
                    />
                  ) : null}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selected Topic Info */}
      {selectedTopic && !editingTopic && (
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="selected-topic-card">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            {((selectedTopic.levels && selectedTopic.levels.length > 0) ? selectedTopic.levels : [selectedTopic.level]).map(lvl => (
              <span key={lvl} className={`level-badge ${lvl}`}>{lvl}</span>
            ))}
            {selectedTopic.topic_type === 'multiple_choice' && (
              <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">Multiple Choice</span>
            )}
            <span className="text-sm text-[#78716C]">{selectedTopic.response_count} Antworten</span>
            {selectedTopic.teacher_responded ? (
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                data-testid="selected-topic-corrected"
                title={selectedTopic.teacher_replied_at ? `Korrigiert am ${new Date(selectedTopic.teacher_replied_at).toLocaleDateString('de-DE')}` : 'Vom Lehrer korrigiert'}
              >
                <CheckCircle size={14} weight="fill" /> Vom Lehrer korrigiert
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                data-testid="selected-topic-pending"
                title="Noch nicht vom Lehrer korrigiert"
              >
                Ausstehend
              </span>
            )}
            {isTeacher && (
              <div className="ml-auto flex items-center gap-2">
                <button onClick={startEditTopic} className="inline-flex items-center gap-1 text-xs font-semibold text-[#57534E] hover:text-[#292524]" data-testid="edit-topic-btn">
                  <PencilSimpleLine size={14} /> Bearbeiten
                </button>
                <button
                  onClick={() => setResendTopic(selectedTopic)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-800"
                  data-testid="resend-topic-btn"
                >
                  <PaperPlaneTilt size={14} weight="duotone" /> An Klassen zuweisen
                </button>
                <button onClick={deleteTopic} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700" data-testid="delete-topic-btn">
                  <Trash size={14} /> Löschen
                </button>
                {selectedTopic.scheduled_at && new Date(selectedTopic.scheduled_at) > new Date() && (
                  <span
                    className="text-[10px] font-bold tracking-wider uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"
                    data-testid={`topic-scheduled-${selectedTopic.topic_id}`}
                    title={`Wird veröffentlicht: ${new Date(selectedTopic.scheduled_at).toLocaleString('de-DE')}`}
                  >
                    {scheduleBadgeText(selectedTopic.scheduled_at)}
                  </span>
                )}
              </div>
            )}
          </div>
          <h2 className="font-heading text-xl font-bold text-[#292524] mb-2">{selectedTopic.title}</h2>
          <p className="text-[#57534E]">{selectedTopic.content}</p>
          {selectedTopic.content_de && <p className="text-[#78716C] text-sm mt-2 italic">{selectedTopic.content_de}</p>}
        </div>
      )}

      {/* Teacher edit-topic panel */}
      {isTeacher && editingTopic && topicDraft && (
        <div className="bg-white rounded-2xl border-2 border-dashed border-[#AB1C24]/50 p-6 space-y-3" data-testid="edit-topic-panel">
          <h3 className="font-heading font-semibold text-lg text-[#292524]">Thema bearbeiten</h3>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setTopicDraft(p => ({ ...p, topic_type: 'open' }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${topicDraft.topic_type === 'open' ? 'bg-[#AB1C24] text-white border-[#AB1C24]' : 'bg-white text-[#57534E] border-stone-300'}`}
            >Offene Frage</button>
            <button
              type="button"
              onClick={() => setTopicDraft(p => ({ ...p, topic_type: 'multiple_choice', options: p.options && p.options.length >= 2 ? p.options : ['', ''] }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${topicDraft.topic_type === 'multiple_choice' ? 'bg-[#AB1C24] text-white border-[#AB1C24]' : 'bg-white text-[#57534E] border-stone-300'}`}
            >Multiple Choice</button>
          </div>
          <Input value={topicDraft.title} onChange={e => setTopicDraft(p => ({ ...p, title: e.target.value }))} placeholder="Titel" />
          <Textarea value={topicDraft.content} onChange={e => setTopicDraft(p => ({ ...p, content: e.target.value }))} className="min-h-[70px]" placeholder="Inhalt" />
          <Textarea value={topicDraft.content_de} onChange={e => setTopicDraft(p => ({ ...p, content_de: e.target.value }))} className="min-h-[50px]" placeholder="Deutsche Übersetzung (optional)" />
          <Input value={topicDraft.prompt_question} onChange={e => setTopicDraft(p => ({ ...p, prompt_question: e.target.value }))} placeholder="Frage an die Teilnehmer" />
          {topicDraft.topic_type === 'multiple_choice' && (
            <div className="space-y-2 bg-[#FAF9F6] rounded-xl p-3">
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Optionen</p>
              {topicDraft.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTopicDraft(p => ({ ...p, correct_option: p.correct_option === i ? null : i }))}
                    className={`w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${topicDraft.correct_option === i ? 'bg-green-600 border-green-600 text-white' : 'border-stone-300 text-stone-400'}`}
                  ><CheckCircle size={14} weight="fill" /></button>
                  <Input value={opt} onChange={e => {
                    const opts = [...topicDraft.options]; opts[i] = e.target.value;
                    setTopicDraft(p => ({ ...p, options: opts }));
                  }} placeholder={`Option ${i + 1}`} />
                  {topicDraft.options.length > 2 && (
                    <button type="button" onClick={() => setTopicDraft(p => ({
                      ...p,
                      options: p.options.filter((_, j) => j !== i),
                      correct_option: p.correct_option === i ? null : p.correct_option !== null && p.correct_option > i ? p.correct_option - 1 : p.correct_option,
                    }))} className="text-red-400 hover:text-red-600 p-1"><XIcon size={14} /></button>
                  )}
                </div>
              ))}
              {topicDraft.options.length < 6 && (
                <Button type="button" variant="outline" size="sm" onClick={() => setTopicDraft(p => ({ ...p, options: [...p.options, ''] }))}>
                  <Plus size={14} className="mr-1" /> Option hinzufügen
                </Button>
              )}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Niveaus (mehrere auswählbar)</p>
            <LevelPills value={topicDraft.levels} onChange={v => setTopicDraft(p => ({ ...p, levels: v }))} testIdPrefix="edit-topic-level" />
          </div>
          <div>
            <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">{scheduleFieldLabel(selectedTopic?.scheduled_at)}</p>
            <Input type="datetime-local" value={topicDraft.scheduled_at || ''} onChange={e => setTopicDraft(p => ({ ...p, scheduled_at: e.target.value }))} data-testid="edit-topic-scheduled-at" />
            {isDraftDate(selectedTopic?.scheduled_at) && <p className="text-[11px] text-[#78716C] mt-1">{DRAFT_HINT}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={saveEditTopic} className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1.5" data-testid="save-edit-topic-btn">
              <FloppyDisk size={14} weight="fill" /> Speichern
            </Button>
            <Button variant="outline" onClick={() => setEditingTopic(false)} className="gap-1.5">
              <XIcon size={14} /> Abbrechen
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <TabsList className={`w-full max-w-md bg-[#F5F5F4] p-1 rounded-xl ${isWriting ? 'hidden' : ''}`}>
            <TabsTrigger 
              value="written" 
              className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#AB1C24]"
              data-testid="written-tab"
            >
              <PencilLine size={20} className="mr-2" />
              Schriftlich
            </TabsTrigger>
            <TabsTrigger 
              value="voice" 
              className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#AB1C24]"
              data-testid="voice-tab"
            >
              <Microphone size={20} className="mr-2" />
              Mündlich
            </TabsTrigger>
          </TabsList>
          <Select value={String(postLimit)} onValueChange={v => setPostLimit(parseInt(v, 10))}>
            <SelectTrigger className="w-[190px]" data-testid="post-limit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">Letzte 10 Nachrichten</SelectItem>
              <SelectItem value="50">Letzte 50 Nachrichten</SelectItem>
              <SelectItem value="100">Letzte 100 Nachrichten</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Written Posts Tab */}
        <TabsContent value="written" className="mt-6 space-y-6">
          {/* New Post Form */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
            {selectedTopic?.topic_type === 'multiple_choice' && Array.isArray(selectedTopic.options) && (
              <div className="mb-4 space-y-2" data-testid="mc-answer-options">
                <p className="text-sm font-semibold text-[#292524]">Wähle deine Antwort:</p>
                {selectedTopic.options.map((opt, i) => (
                  <label
                    key={i}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedMcOption === i ? 'border-[#AB1C24] bg-[#FDF0ED]' : 'border-stone-200 hover:border-stone-400'}`}
                    data-testid={`mc-answer-${i}`}
                  >
                    <input
                      type="radio"
                      name="mc-answer"
                      checked={selectedMcOption === i}
                      onChange={() => setSelectedMcOption(i)}
                      className="accent-[#AB1C24]"
                    />
                    <span className="text-sm text-[#292524]">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            <Textarea
              placeholder={selectedTopic?.topic_type === 'multiple_choice' ? 'Erkläre deine Wahl auf Spanisch (optional)...' : 'Teile deine Gedanken auf Spanisch...'}
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              className={`min-h-[100px] rounded-xl border-[#D6D3D1] focus:border-[#AB1C24] ${isWriting ? 'mb-1' : 'mb-4'}`}
              data-testid="new-post-textarea"
            />
            {isWriting && !isTeacher && (
              <WritingMilestoneMeter
                level={user?.spanish_level || selectedTopic?.level || 'A2'}
                text={newPost}
              />
            )}
            <div className="flex justify-end">
              <Button
                onClick={handleSubmitPost}
                disabled={submitting}
                className="bg-[#AB1C24] hover:bg-[#8A151C]"
                data-testid="submit-post-btn"
              >
                {submitting ? (
                  <SpinnerGap size={20} className="animate-spin mr-2" />
                ) : (
                  <PaperPlaneTilt size={20} className="mr-2" />
                )}
                Veröffentlichen
              </Button>
            </div>
          </div>

          {/* Posts List with threaded replies */}
          <div className="space-y-4">
            {writtenPosts.length > 0 ? (
              writtenPosts.map((post) => {
                const isOwner = user?.user_id === post.user_id;
                const isPostTeacher = post.user_role === 'admin' || post.user_role === 'teacher';
                const mcOpt = post.selected_option;
                const mcOptLabel = (mcOpt !== null && mcOpt !== undefined && selectedTopic?.options) ? selectedTopic.options[mcOpt] : null;
                const isMcCorrect = selectedTopic?.topic_type === 'multiple_choice' && selectedTopic.correct_option !== null && selectedTopic.correct_option === mcOpt;
                return (
                  <div key={post.post_id} className="forum-post" data-testid={`written-post-${post.post_id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${isPostTeacher ? 'bg-amber-600' : 'bg-[#AB1C24]'}`}>
                        {post.user_avatar ? (
                          <img src={avatarUrl(post.user_avatar)} alt={post.user_name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          post.user_name?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-[#292524]">{post.user_name}</span>
                          {isPostTeacher && <span className="text-[10px] uppercase font-bold tracking-wider bg-[#AB1C24] text-white px-1.5 py-0.5 rounded">Profesor</span>}
                          <span className="text-xs text-[#78716C]">
                            {new Date(post.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {post.edited_at && <span className="text-[10px] text-[#A8A29E] italic">· bearbeitet</span>}
                        </div>
                        {mcOptLabel && (
                          <div className={`mb-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full border ${isMcCorrect ? 'text-green-800 bg-green-50 border-green-200' : 'text-stone-700 bg-stone-50 border-stone-200'}`}>
                            {isMcCorrect && <CheckCircle size={12} weight="fill" />}
                            Antwort: {mcOptLabel}
                          </div>
                        )}
                        {editingPostId === post.post_id ? (
                          <div className="space-y-2">
                            <Textarea value={editPostText} onChange={e => setEditPostText(e.target.value)} rows={3} />
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1" onClick={() => savePostEdit(post.post_id)} data-testid={`save-post-${post.post_id}`}>
                                <FloppyDisk size={12} weight="fill" /> Speichern
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingPostId(null)}>Abbrechen</Button>
                            </div>
                          </div>
                        ) : (
                          post.content && <p className="text-[#57534E] break-words whitespace-pre-wrap">{post.content}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 flex-wrap">
                          <button
                            onClick={() => handleLike(post.post_id)}
                            className="flex items-center gap-1 text-sm text-[#78716C] hover:text-[#AB1C24] transition-colors"
                          >
                            <Heart size={18} />
                            <span>{post.likes}</span>
                          </button>
                          <button
                            onClick={() => { setReplyTo(replyTo === post.post_id ? null : post.post_id); setReplyText(''); }}
                            className="flex items-center gap-1 text-sm text-[#78716C] hover:text-[#5B8C9A] transition-colors"
                            data-testid={`reply-btn-${post.post_id}`}
                          >
                            <ChatCircle size={18} />
                            <span>{isTeacher && !isOwner ? 'Responder como Profesor' : 'Antworten'}</span>
                          </button>
                          {(isOwner || isTeacher) && (
                            <>
                              <button onClick={() => { setEditingPostId(post.post_id); setEditPostText(post.content || ''); }} className="flex items-center gap-1 text-sm text-[#78716C] hover:text-[#292524] transition-colors" data-testid={`edit-post-${post.post_id}`}>
                                <PencilSimpleLine size={16} />
                              </button>
                              <button onClick={() => deletePost(post.post_id)} className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-post-${post.post_id}`}>
                                <Trash size={16} />
                              </button>
                            </>
                          )}
                          {/* KI-Feedback button — only in Schreiben mode and only for the post owner / teacher. */}
                          {isWriting && (isOwner || isTeacher) && (
                            <AiFeedbackButton postId={post.post_id} cachedFeedback={post.ai_feedback} />
                          )}
                        </div>

                        {/* Inline reply form */}
                        {replyTo === post.post_id && (
                          <div className="mt-3 ml-2 pl-3 border-l-2 border-[#AB1C24]/40 space-y-2">
                            <Textarea
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              placeholder={isTeacher ? 'Tu respuesta como profesor…' : 'Tu respuesta…'}
                              rows={2}
                              data-testid={`reply-input-${post.post_id}`}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1" onClick={() => submitReply(post.post_id)} data-testid={`submit-reply-${post.post_id}`}>
                                <PaperPlaneTilt size={12} weight="fill" /> Senden
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setReplyTo(null)}>Abbrechen</Button>
                            </div>
                          </div>
                        )}

                        {/* Threaded replies */}
                        {(post.replies && post.replies.length > 0) && (
                          <div className="mt-3 ml-2 pl-3 border-l-2 border-stone-200 space-y-3" data-testid={`replies-${post.post_id}`}>
                            {post.replies.map(r => {
                              const isReplyTeacher = r.user_role === 'admin' || r.user_role === 'teacher';
                              const canModReply = user?.user_id === r.user_id || isTeacher;
                              return (
                                <div key={r.post_id} className="flex items-start gap-2" data-testid={`reply-${r.post_id}`}>
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden ${isReplyTeacher ? 'bg-amber-600' : 'bg-stone-400'}`}>
                                    {r.user_avatar ? (
                                      <img src={avatarUrl(r.user_avatar)} alt={r.user_name} className="w-full h-full rounded-full object-cover" />
                                    ) : (
                                      r.user_name?.charAt(0).toUpperCase()
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-medium text-[#292524]">{r.user_name}</span>
                                      {isReplyTeacher && <span className="text-[9px] uppercase font-bold tracking-wider bg-[#AB1C24] text-white px-1 py-0.5 rounded">Profesor</span>}
                                      <span className="text-[11px] text-[#A8A29E]">{new Date(r.created_at).toLocaleString('de-DE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                      {r.edited_at && <span className="text-[10px] text-[#A8A29E] italic">· bearb.</span>}
                                    </div>
                                    {editingPostId === r.post_id ? (
                                      <div className="space-y-1 mt-1">
                                        <Textarea value={editPostText} onChange={e => setEditPostText(e.target.value)} rows={2} />
                                        <div className="flex gap-1">
                                          <Button size="sm" className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1 h-7 text-xs" onClick={() => savePostEdit(r.post_id)}><FloppyDisk size={11} weight="fill" /> OK</Button>
                                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingPostId(null)}>Abbr.</Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-[#57534E] break-words whitespace-pre-wrap">{r.content}</p>
                                    )}
                                    {canModReply && editingPostId !== r.post_id && (
                                      <div className="flex items-center gap-2 mt-1">
                                        <button onClick={() => { setEditingPostId(r.post_id); setEditPostText(r.content || ''); }} className="text-xs text-[#78716C] hover:text-[#292524]"><PencilSimpleLine size={12} /></button>
                                        <button onClick={() => deletePost(r.post_id)} className="text-xs text-red-400 hover:text-red-600"><Trash size={12} /></button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-[#78716C]">
                Noch keine schriftlichen Beiträge. Sei der Erste!
              </div>
            )}
          </div>
        </TabsContent>

        {/* Voice Posts Tab */}
        <TabsContent value="voice" className="mt-6 space-y-6">
          {/* Voice Recording */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
            <div className="flex flex-col items-center gap-4">
              {!audioUrl ? (
                <>
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`recording-btn ${isRecording ? 'recording' : 'idle'}`}
                    data-testid="record-voice-btn"
                  >
                    {isRecording ? <Stop size={28} weight="fill" /> : <Microphone size={28} />}
                  </button>
                  <p className="text-[#57534E]">
                    {isRecording ? 'Aufnahme läuft... Klicke zum Stoppen' : 'Klicke zum Aufnehmen'}
                  </p>
                  {isRecording && (
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className="w-1 bg-[#AB1C24] rounded-full waveform-bar" />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="voice-player w-full max-w-md">
                    <button
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.pause();
                          audioRef.current = null;
                        }
                        const audio = registerAudio(new Audio(audioUrl));
                        audioRef.current = audio;
                        audio.onended = () => { audioRef.current = null; };
                        audio.play();
                      }}
                      className="w-10 h-10 rounded-full bg-[#AB1C24] text-white flex items-center justify-center"
                    >
                      <Play size={20} weight="fill" />
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-1 h-6">
                        {[...Array(20)].map((_, i) => (
                          <div 
                            key={i} 
                            className="w-1 bg-[#AB1C24]/40 rounded-full"
                            style={{ height: `${Math.random() * 100}%` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <Textarea
                    placeholder="Optional: Schreibe etwas dazu (Erklärung, Transkription...)"
                    value={voiceCaption}
                    onChange={e => setVoiceCaption(e.target.value)}
                    rows={2}
                    className="rounded-xl border-[#D6D3D1] focus:border-[#AB1C24]"
                    data-testid="voice-caption-input"
                  />
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setAudioBlob(null); setAudioUrl(null); setVoiceCaption(''); }}
                    >
                      Neu aufnehmen
                    </Button>
                    <Button
                      onClick={submitVoicePost}
                      disabled={submitting}
                      className="bg-[#AB1C24] hover:bg-[#8A151C]"
                      data-testid="submit-voice-btn"
                    >
                      {submitting ? <SpinnerGap size={20} className="animate-spin mr-2" /> : null}
                      Senden
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Voice Posts List */}
          <div className="space-y-4">
            {voicePosts.length > 0 ? (
              voicePosts.map((post) => {
                const isVoiceTeacher = post.user_role === 'admin' || post.user_role === 'teacher';
                const canModVoice = user?.user_id === post.user_id || isTeacher;
                return (
                <div key={post.post_id} className="forum-post" data-testid={`voice-post-${post.post_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${isVoiceTeacher ? 'bg-amber-600' : 'bg-[#5B8C9A]'}`}>
                      {post.user_avatar ? (
                        <img src={avatarUrl(post.user_avatar)} alt={post.user_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        post.user_name?.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-medium text-[#292524]">{post.user_name}</span>
                        {isVoiceTeacher && <span className="text-[10px] uppercase font-bold tracking-wider bg-[#AB1C24] text-white px-1.5 py-0.5 rounded">Profesor</span>}
                        <span className="text-xs text-[#78716C]">
                          {new Date(post.created_at).toLocaleDateString('de-DE')}
                        </span>
                        {canModVoice && (
                          <button
                            onClick={() => deletePost(post.post_id)}
                            className="ml-auto p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50"
                            title="Löschen"
                            data-testid={`delete-voice-${post.post_id}`}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                      <div className="voice-player">
                        <button
                          onClick={() => playVoice(post.post_id, post.storage_path)}
                          className="w-10 h-10 rounded-full bg-[#5B8C9A] text-white flex items-center justify-center"
                        >
                          {isPlaying === post.post_id ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
                        </button>
                        <div className="flex-1 flex items-center gap-1 h-6">
                          {[...Array(15)].map((_, i) => (
                            <div 
                              key={i} 
                              className={`w-1 rounded-full transition-all ${
                                isPlaying === post.post_id ? 'bg-[#5B8C9A] waveform-bar' : 'bg-[#5B8C9A]/40'
                              }`}
                              style={{ height: `${Math.random() * 100}%` }}
                            />
                          ))}
                        </div>
                      </div>
                      {post.content && (
                        <p className="text-sm text-[#57534E] mt-2 whitespace-pre-wrap">{post.content}</p>
                      )}
                    </div>
                  </div>
                </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-[#78716C]">
                Noch keine Sprachaufnahmen. Sei der Erste!
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Teacher-only: resend a Schreiben topic to other classes. */}
      {resendTopic && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !resendBusy && setResendTopic(null)} data-testid="topic-resend-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-lg text-[#292524]">An weitere Klassen zuweisen</h3>
                <p className="text-sm text-[#78716C] mt-1 truncate">{'"'}{resendTopic.title || '(ohne Titel)'}{'"'}</p>
              </div>
              <button onClick={() => !resendBusy && setResendTopic(null)} className="p-1 rounded-lg hover:bg-stone-100"><XIcon size={18} /></button>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setResendSel(prev => {
                  const s = new Set(prev);
                  if (s.has('__ALL__')) s.delete('__ALL__'); else s.add('__ALL__');
                  return s;
                })}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendSel.has('__ALL__') ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                data-testid="topic-resend-target-__ALL__"
              >
                <span className="font-semibold text-[#292524]">Alle Klassen</span>
              </button>
              {groupsList.map(g => (
                <button
                  key={g.group_id}
                  type="button"
                  onClick={() => setResendSel(prev => {
                    const s = new Set(prev);
                    if (s.has(g.group_id)) s.delete(g.group_id); else s.add(g.group_id);
                    return s;
                  })}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendSel.has(g.group_id) ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                  data-testid={`topic-resend-target-${g.group_id}`}
                >
                  <span className="font-semibold text-[#292524]">{g.name}</span>
                  {g.level && <span className="ml-2 text-xs text-[#78716C]">· {g.level}</span>}
                </button>
              ))}
              {groupsList.length === 0 && (
                <p className="text-xs text-[#A8A29E] italic px-1">Keine Klassen definiert.</p>
              )}
            </div>
            <div className="flex gap-2 pt-2 border-t border-stone-200">
              <Button variant="outline" onClick={() => setResendTopic(null)} disabled={resendBusy} className="flex-1" data-testid="topic-resend-cancel">Abbrechen</Button>
              <Button
                onClick={async () => {
                  const targets = Array.from(resendSel);
                  if (targets.length === 0) return toast.error('Bitte mindestens eine Klasse auswählen');
                  setResendBusy(true);
                  try {
                    await axios.post(`${API}/topics/${resendTopic.topic_id}/resend`,
                      { group_ids: targets }, { withCredentials: true });
                    toast.success(`An ${targets.length} Klasse${targets.length === 1 ? '' : 'n'} zugewiesen`);
                    setResendTopic(null);
                    await loadTopics();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Fehler');
                  } finally { setResendBusy(false); }
                }}
                disabled={resendBusy || resendSel.size === 0}
                className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white"
                data-testid="topic-resend-submit"
              >
                {resendBusy ? <SpinnerGap size={14} className="animate-spin mr-2" /> : <PaperPlaneTilt size={14} className="mr-2" weight="fill" />}
                An {resendSel.size} senden
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
